import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle, Loader, Wallet, AlertCircle, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';

import {
  useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitState,
} from '@reown/appkit/react';
import { useWriteContract, useSwitchChain } from 'wagmi';
import { parseUnits } from 'viem';

import {
  useGetUpgradeStatusQuery,
  useVerifyUpgradeTransactionMutation,
  useUseCreditForUpgradeMutation,
} from '../../store/api/planApi';
import { formatUSD } from '../../utils/formatAmount';
import './PremiumCheckout.css';

/**
 * Premium checkout.
 *
 * One way to pay: connect a wallet through Reown/WalletConnect and sign the
 * transfer here. The checkout then holds the transaction hash, so the backend
 * can confirm it straight from the chain (the Alchemy webhook is a backstop).
 *
 * There is deliberately no QR or copy-the-address option. Neither gives the
 * checkout a hash, so both depended entirely on webhook delivery -- and a
 * hand-typed amount is how a charge ends up short.
 *
 * USDT on Ethereum or BNB Chain only. Everything about the chain -- id, token
 * contract, decimals -- comes off the charge the server created, so the UI
 * cannot quietly disagree with what the backend will accept.
 */

// Minimal ERC-20 transfer, the same shape the payment page uses.
const ERC20_TRANSFER_ABI = [
  {
    type: 'function',
    name: 'transfer',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
];

const PremiumCheckout = ({ charge: initial, onClose, onConfirmed }) => {
  const [signing, setSigning] = useState(false);
  const confirmedRef = useRef(false);

  const { open: openWalletModal } = useAppKit();
  const { open: modalOpen } = useAppKitState();
  const { address: walletAddress, isConnected } = useAppKitAccount();
  const { chainId: walletChainId } = useAppKitNetwork();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  // Poll while the charge is open, so a confirmation that arrives by the webhook
  // rather than this panel's own verify call is still picked up.
  const { data: live } = useGetUpgradeStatusQuery(initial.paymentId, {
    pollingInterval: 4000,
    skipPollingIfUnfocused: false,
  });

  // Hash of the transfer this panel's wallet sent. Verified directly against the
  // chain, so the wallet path does not depend on the webhook arriving.
  const [sentTx, setSentTx] = useState(null);
  const [verifyTx] = useVerifyUpgradeTransactionMutation();

  // Result of paying with credit. Used straight away so the panel does not sit
  // on the old state until the next status poll.
  const [applyCredit, { data: creditResult, isLoading: applyingCredit }] = useUseCreditForUpgradeMutation();

  const charge = creditResult || live || initial;
  const status = charge.status || 'pending';
  const isConfirmed = status === 'confirmed';
  const isPartial = status === 'partially_paid';
  const isDead = status === 'expired' || status === 'failed';

  const price = Number(charge.amountUsd) || 0;
  const paid = Number(charge.amountPaidUsd) || 0;
  const creditApplied = Number(charge.creditAppliedUsd) || 0;
  const due = Number(charge.amountDueUsd ?? price);
  const progressPct = price > 0 ? Math.min(100, ((paid + creditApplied) / price) * 100) : 0;
  // Nothing to send: credit covers the whole charge. The merchant still has to
  // say yes -- the backend never spends credit by itself.
  const coveredByCredit = !isConfirmed && !isDead && due <= 0 && creditApplied > 0;
  const paidWithCredit = paid === 0 && creditApplied > 0;

  const payWithCredit = async () => {
    try {
      await applyCredit(initial.paymentId).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || 'Could not apply your credit');
    }
  };

  const paysItself =
    !!walletAddress &&
    !!charge.payToAddress &&
    walletAddress.toLowerCase() === charge.payToAddress.toLowerCase();

  // Ask the backend to read the receipt until it is mined and credited. The
  // status poll above picks up the confirmation either way; this just makes it
  // happen without the webhook. Gives up after ~10 minutes -- the webhook is
  // still watching after that.
  useEffect(() => {
    if (!sentTx || isConfirmed) return undefined;
    let cancelled = false;
    let attempts = 0;
    let timer;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await verifyTx({ paymentId: initial.paymentId, txHash: sentTx }).unwrap();
        if (cancelled || res?.status === 'confirmed') return;
      } catch (err) {
        if (cancelled) return;
        // A 400 means the chain says this transfer does not pay the charge.
        if (err?.status === 400) {
          toast.error(err?.data?.message || 'That transaction does not pay this charge');
          return;
        }
      }
      if (attempts < 120) timer = setTimeout(tick, 5000);
    };

    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sentTx, isConfirmed, initial.paymentId, verifyTx]);

  useEffect(() => {
    if (isConfirmed && !confirmedRef.current) {
      confirmedRef.current = true;
      onConfirmed?.();
    }
  }, [isConfirmed, onConfirmed]);

  // ── Connect ────────────────────────────────────────────────────────────────
  // Same two-step flow as the invoice payment page: Connect wallet only opens the
  // modal; paying is a separate, explicit click once an address is showing.
  //
  // The modal can take a moment to appear the first time (Reown loads its UI and
  // wallet list on demand), so the button shows "Opening wallet..." from the
  // click until the modal is actually up, instead of looking dead.
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (modalOpen || isConnected) setOpening(false);
  }, [modalOpen, isConnected]);
  // Never leave the button stuck if the modal fails to appear at all.
  useEffect(() => {
    if (!opening) return undefined;
    const t = setTimeout(() => setOpening(false), 15000);
    return () => clearTimeout(t);
  }, [opening]);

  const connectWallet = () => {
    setOpening(true);
    openWalletModal({ view: 'Connect' });
  };

  // Lets the merchant swap wallets or disconnect, from the address chip.
  const manageWallet = () => openWalletModal({ view: 'Account' });

  // Once a wallet connects, move it to the charge's chain straight away, so the
  // Pay click is only the transfer signature.
  //
  // Exactly ONCE per wallet+chain. The ref used to be cleared whenever the
  // wallet reported the right chain, so the moment the merchant picked another
  // network in MetaMask -- or the reported chain id flickered during the switch
  // -- it switched again, and again, stacking "Switched to ..." toasts. A later
  // network change is the merchant's choice; payFromWallet switches back when
  // they press Pay.
  const autoSwitchRef = useRef(null);
  useEffect(() => {
    if (!isConnected || !walletChainId || !charge.chainId) return;
    const key = `${walletAddress}-${charge.chainId}`;
    if (autoSwitchRef.current === key) return;
    autoSwitchRef.current = key;
    if (Number(walletChainId) === Number(charge.chainId)) return;
    switchChainAsync({ chainId: Number(charge.chainId) })
      // Fixed id: even if it fires twice, there is one toast on screen.
      .then(() => toast.success(`Switched to ${charge.networkLabel}`, { id: 'premium-chain-switch' }))
      .catch(() => { /* declined -- payFromWallet asks again on Pay */ });
  }, [isConnected, walletAddress, walletChainId, charge.chainId, charge.networkLabel, switchChainAsync]);

  const onWrongChain =
    isConnected && !!walletChainId && !!charge.chainId &&
    Number(walletChainId) !== Number(charge.chainId);

  // ── Pay ────────────────────────────────────────────────────────────────────
  const payFromWallet = async () => {
    if (!isConnected) {
      connectWallet();
      return;
    }
    // Sending from the receiving wallet to itself moves nothing, so it can never
    // pay the charge -- it just burns gas.
    if (paysItself) {
      toast.error('This wallet is the payment address. Connect a different wallet to pay.');
      return;
    }
    setSigning(true);
    try {
      // The charge names the chain. Signing on the wrong one would send real
      // USDT to the right address on a chain nothing is watching.
      if (Number(walletChainId) !== Number(charge.chainId)) {
        try {
          await switchChainAsync({ chainId: Number(charge.chainId) });
        } catch (switchErr) {
          const m = String(switchErr?.shortMessage || switchErr?.message || '');
          toast.error(/rejected|denied|4001/i.test(m)
            ? 'Network switch rejected.'
            : `Switch your wallet to ${charge.networkLabel} to pay.`);
          return;
        }
      }

      toast.loading('Please sign the transaction in your wallet...', { id: 'premium-sign' });
      const amount = parseUnits(String(due), charge.tokenDecimals);
      const txHash = await writeContractAsync({
        address: charge.tokenContract,
        abi: ERC20_TRANSFER_ABI,
        functionName: 'transfer',
        args: [charge.payToAddress, amount],
      });
      toast.dismiss('premium-sign');

      toast.success(`Transaction sent! ${txHash.slice(0, 10)}...${txHash.slice(-8)}`);
      // Nothing is marked paid here. The charge is credited when the backend
      // reads the transfer on chain, which is the only evidence it landed.
      setSentTx(txHash);
    } catch (err) {
      toast.dismiss('premium-sign');
      const msg = String(err?.shortMessage || err?.message || '');
      if (/user rejected|User denied|4001/i.test(msg)) toast.error('Transaction rejected by user.');
      else if (/insufficient funds|exceeds balance/i.test(msg)) toast.error(`Not enough ${charge.token} or gas in this wallet.`);
      else toast.error(msg.split('\n')[0] || 'Could not send the payment');
    } finally {
      setSigning(false);
    }
  };

  if (isConfirmed) {
    const over = Math.max(0, paid + creditApplied - price);
    return (
      <div className="rd-card rd-card-pad pc-panel">
        <div className="pc-success">
          <CheckCircle size={40} />
          <h2>You are now on Premium</h2>
          <p>Your 1% payout fee and lower minimum payout fees are active.</p>
          {paidWithCredit && (
            <p className="pc-credit-note">
              Paid with ${formatUSD(creditApplied)} of your credit. No payment was taken.
            </p>
          )}
          {over > 0.004 && (
            <p className="pc-credit-note">
              You sent ${formatUSD(over)} more than the price. It is held as credit and
              comes off your next bill automatically.
            </p>
          )}
          {charge.receiptNumber && (
            <p className="pc-receipt">Receipt {charge.receiptNumber}</p>
          )}
          <button className="rd-btn rd-btn-purple" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rd-card rd-card-pad pc-panel">
      <div className="pc-head">
        <h2>{coveredByCredit ? 'Covered by your credit' : 'Pay with USDT to upgrade'}</h2>
        {!coveredByCredit && (
          <span className="pc-chain">{charge.networkLabel} &middot; {charge.token}</span>
        )}
      </div>

      <div className="pc-body">
      {/* The amounts before anything else: what it costs, what credit took off
          it, what has arrived, and what is left to send. */}
      <div className="pc-amounts">
        <div className="pc-line">
          <span>Premium (monthly)</span>
          <span className="rd-mono">${formatUSD(price)}</span>
        </div>
        {creditApplied > 0 && (
          <div className="pc-line pc-line--credit">
            <span>Credit from previous overpayment</span>
            <span className="rd-mono">-${formatUSD(creditApplied)}</span>
          </div>
        )}
        {paid > 0 && (
          <div className="pc-line pc-line--credit">
            <span>Already received</span>
            <span className="rd-mono">-${formatUSD(paid)}</span>
          </div>
        )}
        <div className="pc-line pc-line--total">
          <span>{paid > 0 ? 'Still to pay' : 'To pay'}</span>
          <span className="rd-mono">${formatUSD(due)} {charge.token}</span>
        </div>
      </div>

      {isPartial && (
        <div className="pc-partial">
          <AlertCircle size={16} />
          <div>
            <strong>Payment incomplete</strong>
            <span>
              ${formatUSD(paid)} of ${formatUSD(price)} received. Send the remaining
              ${formatUSD(due)} to the same address to finish. Premium activates as soon
              as it lands.
            </span>
            <div className="pc-progress" aria-hidden="true">
              <i style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>
      )}

      {isDead && (
        <div className="pc-partial pc-partial--dead">
          <AlertCircle size={16} />
          <div>
            <strong>This charge has expired</strong>
            <span>
              {paid > 0
                ? `The $${formatUSD(paid)} you sent is kept as credit and comes off your next charge.`
                : 'Close this and start again when you are ready.'}
            </span>
          </div>
        </div>
      )}

      {coveredByCredit ? (
        <div className="pc-pane">
          <p className="pc-hint">
            Your credit from earlier overpayments covers this charge in full, so there is
            nothing to send. ${formatUSD(creditApplied)} will be taken from your credit.
          </p>
          <button
            type="button"
            className="rd-btn rd-btn-purple pc-pay-btn"
            onClick={payWithCredit}
            disabled={applyingCredit}
          >
            {applyingCredit
              ? <><Loader size={16} className="pc-spin" /> Applying credit...</>
              : `Use $${formatUSD(creditApplied)} credit`}
          </button>
        </div>
      ) : (
      <div className="pc-pane">
        {!isConnected ? (
          /* Step 1: connect. This button only opens the wallet modal. */
          <>
            <p className="pc-hint">
              Connect a browser or mobile wallet to pay {formatUSD(due)} {charge.token} on{' '}
              {charge.networkLabel}.
            </p>
            <button
              type="button"
              className="rd-btn rd-btn-purple pc-pay-btn"
              onClick={connectWallet}
              disabled={opening}
            >
              {opening
                ? <><Loader size={16} className="pc-spin" /> Opening wallet...</>
                : <><Wallet size={16} /> Connect wallet</>}
            </button>
          </>
        ) : (
          /* Step 2: the connected address in place of the connect button, then
             an explicit Pay. */
          <>
            <button
              type="button"
              className="pc-wallet-chip"
              onClick={manageWallet}
              disabled={signing || !!sentTx}
              title="Change or disconnect wallet"
            >
              <span className="pc-wallet-dot" aria-hidden="true" />
              <Wallet size={15} aria-hidden="true" />
              <code className="rd-mono">{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</code>
              <span className="pc-wallet-change">Change</span>
            </button>

            {paysItself && (
              <p className="pc-connected pc-connected--warn">
                This is the wallet that receives the payment. Connect a different wallet to pay.
              </p>
            )}
            {onWrongChain && !paysItself && !sentTx && (
              <p className="pc-connected">
                Your wallet is on another network. Paying will ask to switch to {charge.networkLabel}.
              </p>
            )}

            {/* Locked once sent: the button used to stay live while the
                transfer was being mined, and pressing it again paid the charge
                twice. */}
            <button
              type="button"
              className="rd-btn rd-btn-purple pc-pay-btn"
              onClick={payFromWallet}
              disabled={signing || due <= 0 || paysItself || !!sentTx}
            >
              {sentTx ? <><Loader size={16} className="pc-spin" /> Payment sent, confirming on chain...</>
                : signing ? <><Loader size={16} className="pc-spin" /> Confirm in your wallet...</>
                : <>Pay {formatUSD(due)} {charge.token}</>}
            </button>
          </>
        )}
      </div>
      )}

      <div className="pc-foot">
        {/* {coveredByCredit
          ? <span className="pc-watching">No payment needed</span>
          : <span className="pc-watching"><Loader size={13} className="pc-spin" /> Watching for your payment...</span>} */}
        <button type="button" className="rd-btn rd-btn-ghost rd-btn-sm" onClick={onClose}>Close</button>
      </div>

      {charge.txHash && charge.explorerTx && (
        <a className="pc-explorer" href={`${charge.explorerTx}${charge.txHash}`}
           target="_blank" rel="noreferrer">
          View last transaction <ExternalLink size={12} />
        </a>
      )}
      </div>
    </div>
  );
};

export default PremiumCheckout;
